import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, FileText, Upload, Sparkles, ArrowRight, ScrollText, CalendarClock, Archive } from "lucide-react";
import { listCompanies, listResolutions, downloadCompliancePack } from "@/lib/api";
import { toast } from "sonner";
import NotificationsFeed from "@/components/MCAFeed";

export default function Dashboard() {
  const [companies, setCompanies] = useState([]);
  const [resolutions, setResolutions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listCompanies(), listResolutions()])
      .then(([c, r]) => {
        setCompanies(c.data || []);
        setResolutions(r.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const totalDirectors = companies.reduce((acc, c) => acc + (c.directors?.length || 0), 0);
  const thisFY = (() => {
    const d = new Date();
    const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return `${y}-${String(y + 1).slice(-2)}`;
  })();
  const fyResolutions = resolutions.filter((r) => r.financial_year === thisFY).length;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="dashboard-page">
      {/* Hero */}
      <div className="border border-zinc-200 bg-white relative overflow-hidden mb-10">
        <div className="grid-bg absolute inset-0 opacity-50" />
        <div className="relative p-8 md:p-12 lg:p-16">
          <div className="label-eyebrow mb-4">Compliance · Companies Act 2013 · SS-1</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] max-w-3xl">
            Draft board resolutions
            <br />
            with <span className="text-[#002FA7] font-medium">precision.</span>
          </h1>
          <p className="mt-6 text-zinc-600 max-w-xl leading-relaxed">
            Upload your MCA master data once. Pick a resolution type. Get a numbered,
            signature-ready resolution, notice and minutes — in PDF and Word — in seconds.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/companies/new" className="swiss-btn-primary" data-testid="hero-upload-cta">
              <Upload size={16} /> Upload MCA Master Data
            </Link>
            <Link to="/generate" className="swiss-btn-ghost" data-testid="hero-generate-cta">
              <Sparkles size={16} /> Generate Resolution
            </Link>
            <Link to="/quarterly-pack" className="swiss-btn-ghost" data-testid="hero-quarterly-cta">
              <CalendarClock size={16} /> Quarterly Pack
            </Link>
          </div>
        </div>
      </div>

      {/* Stats — Control Room grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 mb-10 border border-zinc-200" data-testid="stats-grid">
        <StatTile label="Companies" value={loading ? "—" : companies.length} icon={Building2} testId="stat-companies" />
        <StatTile label="Directors on Record" value={loading ? "—" : totalDirectors} icon={ScrollText} testId="stat-directors" borderLeft />
        <StatTile label={`Resolutions · FY ${thisFY}`} value={loading ? "—" : fyResolutions} icon={FileText} testId="stat-resolutions" borderLeft />
      </div>

      {/* Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200 mb-10">
        <div className="bg-white p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-medium tracking-tight">Recent Companies</h2>
            <Link to="/companies" className="text-sm text-[#002FA7] hover:underline flex items-center gap-1" data-testid="view-all-companies">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          {companies.length === 0 ? (
            <EmptyBlock title="No companies yet" cta="Upload MCA PDF" to="/companies/new" testId="empty-companies" />
          ) : (
            <ul className="divide-y divide-zinc-100" data-testid="recent-companies-list">
              {companies.slice(0, 5).map((c) => (
                <li key={c.id} className="py-3 flex items-center justify-between stagger-in">
                  <div>
                    <Link to={`/companies/${c.id}`} className="font-medium hover:text-[#002FA7]" data-testid={`company-link-${c.id}`}>
                      {c.name}
                    </Link>
                    <div className="text-xs text-zinc-500 font-mono-plex mt-0.5">{c.cin || "CIN: —"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={async () => {
                        try {
                          await downloadCompliancePack(c.id);
                          toast.success(`Pre-AGM Pack: ${c.name}`);
                        } catch {
                          toast.error("Could not download pack");
                        }
                      }}
                      className="text-zinc-400 hover:text-[#002FA7] p-1"
                      data-testid={`dashboard-pack-${c.id}`}
                      title="Pre-AGM Pack — download every register PDF as a ZIP."
                    >
                      <Archive size={14} />
                    </button>
                    <span className="label-eyebrow">{c.directors?.length || 0} Directors</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-medium tracking-tight">Recent Resolutions</h2>
            <Link to="/resolutions" className="text-sm text-[#002FA7] hover:underline flex items-center gap-1" data-testid="view-all-resolutions">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          {resolutions.length === 0 ? (
            <EmptyBlock title="No resolutions drafted" cta="Generate first" to="/generate" testId="empty-resolutions" />
          ) : (
            <ul className="divide-y divide-zinc-100" data-testid="recent-resolutions-list">
              {resolutions.slice(0, 5).map((r) => (
                <li key={r.id} className="py-3 flex items-center justify-between stagger-in">
                  <div>
                    <Link to={`/resolutions/${r.id}`} className="font-medium hover:text-[#002FA7]" data-testid={`resolution-link-${r.id}`}>
                      {r.resolution_title}
                    </Link>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      <span className="font-mono-plex">{r.resolution_number}</span> · {r.company_name}
                    </div>
                  </div>
                  <span className="label-eyebrow">{r.meeting_date}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Compliance updates feed */}
      <NotificationsFeed limit={6} />
    </div>
  );
}

function StatTile({ label, value, icon: Icon, borderLeft, testId }) {
  return (
    <div className={`p-8 bg-white ${borderLeft ? "md:border-l border-zinc-200" : ""}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="label-eyebrow">{label}</div>
        <Icon size={18} strokeWidth={1.5} className="text-zinc-400" />
      </div>
      <div className="font-heading text-5xl font-light mt-4 tracking-tight">{value}</div>
    </div>
  );
}

function EmptyBlock({ title, cta, to, testId }) {
  return (
    <div className="text-center py-10 border border-dashed border-zinc-300" data-testid={testId}>
      <p className="text-zinc-500 mb-4 text-sm">{title}</p>
      <Link to={to} className="swiss-btn-primary text-sm" data-testid={`${testId}-cta`}>{cta} <ArrowRight size={14} /></Link>
    </div>
  );
}
